import short from 'short-uuid'

import moment from 'moment'
import firebase from 'firebase/app'
import { Location } from 'history'

import { envConfig } from './envConfig'
import { PlaybookMode, PlaybookType } from 'app/types'

export const isDev = envConfig.firebaseConfig.projectId !== 'guidde-production'
export const isLocalhost = process.env.NODE_ENV === 'development'

export const uuid = short.generate

export const delay = (time: number): Promise<void> =>
    // @ts-ignore
    new Promise(resolve => setTimeout(resolve, time)).then(clearTimeout)

export const host =
    window.location.hostname === 'localhost'
        ? `https://${envConfig.firebaseConfig.projectId}.web.app`
        : ''

export const fetchData = (url: string, requestData: any): any =>
    fetch(`${host}${url}`, requestData).then(res => res.json())

export const jsonError = 'RESPONSE_IN_NOT_JSON'

export const request = async (
    url: string,
    method: string,
    body?: any,
    tokenRetries: number = 3
): Promise<any> => {
    const token = await firebase.auth().currentUser?.getIdToken()

    // Stop recursion after 3 retries
    if (!token && !tokenRetries) return

    // On page load we don't have user initialized so let's wait and try again
    if (!token) {
        return delay(1000).then(() => request(url, method, body, tokenRetries - 1))
    }

    return fetch(`${host}${url}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`
        },
        ...(body && { body: JSON.stringify(body) })
    }).then(handleRequest(method))
}

export const handleRequest = (method: string) => async (res: any) => {
    const errorObject = { status: res.status, message: jsonError, method, url: res.url }
    const contentType = res.headers.get('content-type')

    if (!contentType?.includes('application/json')) {
        throw errorObject
    } else {
        const json = await res.json()

        if (!res.ok) {
            throw json ? { ...json, code: res.status } : errorObject
        }

        return json
    }
}

export const option = (value: string, label: string) => ({ value, label })

export const objectOption = <T>(value: T, label: string) => ({
    value,
    label
})

export type OptionType = ReturnType<typeof option>

export const uniqueArray = <T, K>(a: Array<T>): Array<K> =>
    [...new Set(a.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))

export const round = (number: number) => Math.round(number * 100) / 100

export const humanFileSize = (size: number) => {
    if (!size) return '0 B'

    const i = Math.floor(Math.log(size) / Math.log(1024))

    return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i]
}

export const formatTime = (seconds: number, showHours = false) => {
    const format = (val: number) => `0${Math.floor(val)}`.slice(-2)

    const hours = seconds / 3600
    const minutes = (seconds % 3600) / 60
    const secs = seconds % 60

    const h = hours >= 1 || showHours ? [hours] : []

    return [...h, minutes, secs].map(format).join(':')
}

export const formatDate = (seconds: number) => {
    return new Date(seconds * 1000).toString().substring(4, 16)
}

export const timeToSeconds = (time: any) => {
    if (!time) return 0

    return time
        .split(':')
        .reverse()
        .reduce((prev: number, curr: number, i: number) => prev + curr * Math.pow(60, i), 0)
}

export const tagRegex = new RegExp(/^[a-zA-Z0-9_.-]*$/)

export const roundToHundredth = (value: number) => Number(value.toFixed(2))

export const uiId = (id: string) => `ui-t-${id}`

export const areObjectsEqual = <T, K>(a: T, b: K): boolean =>
    JSON.stringify(a) === JSON.stringify(b)

export const getUserBrowser = () => {
    const sUsrAg = navigator.userAgent
    let sBrowser = 'unknown'

    if (sUsrAg.indexOf('Firefox') > -1) {
        sBrowser = 'firefox'
    } else if (sUsrAg.indexOf('SamsungBrowser') > -1) {
        sBrowser = 'samsung_internet'
    } else if (sUsrAg.indexOf('Opera') > -1 || sUsrAg.indexOf('OPR') > -1) {
        sBrowser = 'opera'
    } else if (sUsrAg.indexOf('Trident') > -1) {
        sBrowser = 'ie'
    } else if (sUsrAg.indexOf('Edg') > -1 || sUsrAg.indexOf('Edge') > -1) {
        sBrowser = 'edge'
    } else if (sUsrAg.indexOf('Chrome') > -1) {
        sBrowser = 'chromium'
    } else if (sUsrAg.indexOf('Safari') > -1) {
        sBrowser = 'safari'
    }
    return sBrowser
}

export const getExtUrl = (forBrowser?: string) => {
    const browser = forBrowser || getUserBrowser()
    const env = envConfig.firebaseConfig.projectId
    const map = {
        edge: {
            'guidde-production':
                'https://microsoftedge.microsoft.com/addons/detail/pldeojbhedianlcaejpfbnckfhkacphe',
            'guidde-dev-staging':
                'https://microsoftedge.microsoft.com/addons/detail/guidde-dev/hocgfmngoiokpfpanmilkknmgoehhien',
            default:
                'https://microsoftedge.microsoft.com/addons/detail/pldeojbhedianlcaejpfbnckfhkacphe'
        },
        chromium: {
            'guidde-production':
                'https://chrome.google.com/webstore/detail/guidde/oacmmmjedhheaijfjidilonpngccnhdl',
            'guidde-dev-staging':
                'https://chrome.google.com/webstore/detail/guidde-stg/jgnijfadmjdidipjnbbmnimpmbiippml',
            default:
                'https://chrome.google.com/webstore/detail/guidde/oacmmmjedhheaijfjidilonpngccnhdl'
        },
        default: {
            default:
                'https://microsoftedge.microsoft.com/addons/detail/pldeojbhedianlcaejpfbnckfhkacphe'
        }
    }
    //@ts-ignore
    const chosenBrowser = map[browser] || map.default

    return chosenBrowser[env] || chosenBrowser.default
}

export const generateSpaceIds = (spaces: Array<string>) => {
    return spaces.filter(it => it !== 'mainSpace')
}

type OpenType = '_blank' | '_self'
export const openLink = (link: string, type: OpenType = '_blank') => {
    window.open(link, type)
}

export const calculateDaysLeft = (endDay: number, startDay: number = Date.now()) => {
    // Take the difference between the dates and divide by milliseconds per day.
    // Round to nearest whole number
    const days = Math.round((endDay - startDay) / (1000 * 60 * 60 * 24))
    return !isNaN(days) ? days : ''
}

export const validateEmail = (email: string) => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
}

export const copyToClipboard = (text?: string) => navigator.clipboard.writeText(text || '')

export const containsEachValue = (arr: Array<unknown>, arr2: Array<unknown>) =>
    arr2?.every(it => arr?.includes(it))

export const capitalize = (str?: string, returnOnlyFirstLetter = false) => {
    if (!str) return ''

    const firstLetter = str.charAt(0).toUpperCase()

    if (returnOnlyFirstLetter) return firstLetter

    return firstLetter + str.slice(1)
}

export const getDateUntilNow = (days: number) => {
    return `${moment(Date.now() - 1000 * 60 * 60 * 24 * days).format('DD MMM, YYYY')} → ${moment(
        Date.now()
    ).format('DD MMM, YYYY')}`
}

export const isColorLight = (color: string) => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000

    return brightness > 155
}

export const filterURLParams = (location: Location, keys: Array<string>) => {
    const newSearch = new URLSearchParams(
        Object.fromEntries(
            Array.from(new URLSearchParams(location.search).entries()).filter(
                ([key]) => !keys.includes(key)
            )
        )
    ).toString()

    return `${location.pathname}?${newSearch}`
}

export const isDeepEqual = (object1: any, object2: any) => {
    const isObject = (object: any) => {
        return object != null && typeof object === 'object'
    }

    if (!isObject(object1) || !isObject(object2)) return false

    const objKeys1 = Object.keys(object1)
    const objKeys2 = Object.keys(object2)

    if (objKeys1.length !== objKeys2.length) return false

    for (let key of objKeys1) {
        const value1 = object1[key]
        const value2 = object2[key]

        const isObjects = isObject(value1) && isObject(value2)

        if ((isObjects && !isDeepEqual(value1, value2)) || (!isObjects && value1 !== value2)) {
            return false
        }
    }
    return true
}

export const getTimeString = (time: number) => {
    const secs = `${parseInt(`${time % 60}`, 10)}`.padStart(2, '0')
    const min = `${parseInt(`${(time / 60) % 60}`, 10)}`.padStart(2, '0')

    return `${min}:${secs}`
}

export const isQG = (playbook: PlaybookType) => playbook.mode === PlaybookMode.QuickGuidde

export const isValidUrl = (urlString: string) => {
    const urlPattern = new RegExp(
        '^(https?:\\/\\/)?' + // validate protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
            '(\\#[-a-z\\d_]*)?$',
        'i'
    ) // validate fragment locator
    return Boolean(urlPattern.test(urlString))
}

export const getTextWidth = (text: string, fontSize: string = '14px') => {
    const tag = document.createElement('div')
    tag.style.position = 'absolute'
    tag.style.zIndex = '0'
    tag.style.left = '-99in'
    tag.style.whiteSpace = 'nowrap'
    tag.style.fontSize = fontSize
    tag.innerHTML = text

    document.body.appendChild(tag)
    const result = tag.clientWidth
    document.body.removeChild(tag)

    return result
}
